import {type ReactNode, useState} from 'react';
import {BarChart3} from 'lucide-react';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
  Progress,
  Spinner,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {READ_LEAD_REPORTS_PERMISSION, useAuth} from '@/context/useAuth';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import {cn} from '@/lib/utils';
import {TablePanel} from '@/components/ui/table-panel';
import {InFilterBar} from '@/components/layout/FilterBarSlot';

function formatPercent(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

// An error outcome's share above this reads as a problem worth a glance, not just a number.
const ERROR_WARN_RATE = 0.2;

type Preset = {label: string; from: () => string};

const PRESETS: Preset[] = [
  {label: 'Last 7 days', from: () => isoDaysAgo(7)},
  {label: 'Last 30 days', from: () => isoDaysAgo(30)},
  {label: 'Last 90 days', from: () => isoDaysAgo(90)},
  // The two previous calendar months and the current one, so each month column is whole.
  {label: 'Last 3 months', from: isoStartOfMonthTwoBack},
  {label: 'All time', from: () => ''}
];

function isoDaysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

function isoStartOfMonthTwoBack(): string {
  const date = new Date();
  date.setMonth(date.getMonth() - 2, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
}

/** One outcome column: the row field it counts, and whether it counts against the analyst. */
export type AccuracyOutcome = {field: string; label: string; error?: boolean};

/** A vertical's or an analyst's figures: the total, the accuracy and one count per outcome field. */
export type AccuracyRow = {total_reviewed: number; accuracy_rate: number} & Record<
  string,
  number | string | null
>;

type Interval = 'week' | 'month';
type RangeQuery = {from?: string; to?: string; interval?: Interval};
type QueryResult = {data?: readonly AccuracyRow[] | undefined; isLoading: boolean};

function count(row: AccuracyRow, field: string): number {
  const value = row[field];
  return typeof value === 'number' ? value : 0;
}

// Sums rows into one overall row. Accuracy is the share of the outcomes that aren't errors.
function totalOf(rows: readonly AccuracyRow[], outcomes: readonly AccuracyOutcome[]): AccuracyRow {
  const total = rows.reduce((sum, row) => sum + row.total_reviewed, 0);
  const counts = Object.fromEntries(
    outcomes.map((o) => [o.field, rows.reduce((sum, row) => sum + count(row, o.field), 0)])
  );
  const accurate = outcomes.filter((o) => !o.error).reduce((sum, o) => sum + counts[o.field]!, 0);
  return {...counts, total_reviewed: total, accuracy_rate: total > 0 ? accurate / total : 0};
}

/**
 Review outcomes for a date range: overall, per vertical, and - for leads only - per analyst. Weekly QA
 and QC Review each pass their own queries and outcome columns. Choosing a vertical narrows the
 overall figures and the analyst breakdown to it. Comparing analysts takes read:reports:leads, as
 Reporting's analyst comparisons do, so without it that table is left out rather than requested and
 refused.
 */
export function ReviewAccuracySummary({
  idPrefix,
  outcomes,
  useVerticalRows,
  useAnalystRows,
  defaultPreset = 'All time',
  showTrend = false
}: {
  readonly idPrefix: string;
  readonly outcomes: readonly AccuracyOutcome[];
  readonly useVerticalRows: (range: RangeQuery, enabled?: boolean) => QueryResult;
  readonly useAnalystRows: (
    query: RangeQuery & {policy_violation?: string},
    enabled: boolean
  ) => QueryResult;
  // A preset's label.
  readonly defaultPreset?: string;
  // Adds the accuracy-over-time table; both hooks must then accept `interval`.
  readonly showTrend?: boolean;
}) {
  const {user} = useAuth();
  const canCompareAnalysts = user?.permissions.includes(READ_LEAD_REPORTS_PERMISSION) ?? false;
  const [from, setFrom] = useState(
    () => PRESETS.find((option) => option.label === defaultPreset)?.from() ?? ''
  );
  const [to, setTo] = useState('');
  const [vertical, setVertical] = useState<string | null>(null);
  // Which preset the date range currently matches; null once either date is edited by hand.
  const [preset, setPreset] = useState<string | null>(defaultPreset);

  const range = {...(from ? {from} : {}), ...(to ? {to} : {})};
  const verticalQuery = useVerticalRows(range);
  const analystQuery = useAnalystRows(
    {...range, ...(vertical ? {policy_violation: vertical} : {})},
    canCompareAnalysts
  );

  const verticalRows = verticalQuery.data ?? [];
  const overall = totalOf(
    vertical ? verticalRows.filter((row) => row.policy_violation === vertical) : verticalRows,
    outcomes
  );

  function applyPreset(option: Preset) {
    setPreset(option.label);
    setFrom(option.from());
    setTo('');
  }

  return (
    <div className="flex flex-col gap-4">
      <InFilterBar>
        <div className="flex flex-wrap items-center gap-2">
          {/*
            A toggle group rather than a row of secondary buttons: a pressed toggle takes a solid
            inverted fill, where the old aria-pressed muted tint was indistinguishable from unpressed.
            Nothing is pressed once either date has been edited by hand.
          */}
          <ToggleGroup
            variant="outline"
            spacing={0}
            size="sm"
            aria-label="Date range"
            className="flex-wrap"
            value={preset === null ? [] : [preset]}
            onValueChange={(next) => {
              // Pressing the active preset again would clear the group; the range must always be
              // something, so that press is ignored and the preset stays selected.
              const option = PRESETS.find((p) => p.label === next[0]);
              if (option) {
                applyPreset(option);
              }
            }}>
            {PRESETS.map((option) => (
              <ToggleGroupItem key={option.label} value={option.label}>
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
            <Field orientation="horizontal" className="w-auto gap-1.5">
              <FieldLabel htmlFor={`${idPrefix}-vertical`}>Vertical</FieldLabel>
              <NativeSelect
                id={`${idPrefix}-vertical`}
                size="sm"
                value={vertical ?? ''}
                onChange={(event) => {
                  setVertical(event.target.value === '' ? null : event.target.value);
                }}>
                <NativeSelectOption value="">All verticals</NativeSelectOption>
                {verticalRows.map((row) => (
                  <NativeSelectOption
                    key={String(row.policy_violation)}
                    value={String(row.policy_violation)}>
                    {row.policy_violation}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
            </Field>
            <Field orientation="horizontal" className="w-auto gap-1.5">
              <FieldLabel htmlFor={`${idPrefix}-from`}>From</FieldLabel>
              <Input
                id={`${idPrefix}-from`}
                type="date"
                value={from}
                className="w-40"
                onChange={(event) => {
                  setPreset(null);
                  setFrom(event.target.value);
                }}
              />
            </Field>
            <Field orientation="horizontal" className="w-auto gap-1.5">
              <FieldLabel htmlFor={`${idPrefix}-to`}>To</FieldLabel>
              <Input
                id={`${idPrefix}-to`}
                type="date"
                value={to}
                className="w-40"
                onChange={(event) => {
                  setPreset(null);
                  setTo(event.target.value);
                }}
              />
            </Field>
          </div>
        </div>
      </InFilterBar>

      <section
        aria-label="Overall"
        className="grid grid-cols-2 gap-3 md:auto-cols-fr md:grid-flow-col">
        <Stat label={vertical ? `Reviewed · ${vertical}` : 'Reviewed'}>
          {overall.total_reviewed.toLocaleString()}
        </Stat>
        {outcomes.map((o) => (
          <Stat key={o.field} label={o.label}>
            {count(overall, o.field).toLocaleString()}
          </Stat>
        ))}
        <Stat label="Accuracy">
          {overall.total_reviewed > 0 ? formatPercent(overall.accuracy_rate) : '—'}
        </Stat>
      </section>

      <AccuracyTable
        title="By vertical"
        nameHeader="Vertical"
        isLoading={verticalQuery.isLoading}
        outcomes={outcomes}
        rows={verticalRows.map((row) => ({...row, key: String(row.policy_violation)}))}
        selectedKey={vertical}
        onSelect={(key) => {
          setVertical(key === vertical ? null : key);
        }}
      />

      {showTrend ? (
        <AccuracyTrendTable
          range={range}
          vertical={vertical}
          outcomes={outcomes}
          canCompareAnalysts={canCompareAnalysts}
          useVerticalRows={useVerticalRows}
          useAnalystRows={useAnalystRows}
        />
      ) : null}

      {canCompareAnalysts ? (
        <AccuracyTable
          title={vertical ? `By analyst · ${vertical}` : 'By analyst'}
          nameHeader="Analyst"
          isLoading={analystQuery.isLoading}
          outcomes={outcomes}
          rows={(analystQuery.data ?? []).map((row) => ({...row, key: String(row.analyst_email)}))}
        />
      ) : null}
    </div>
  );
}

function periodLabel(periodStart: string, interval: Interval): string {
  const date = new Date(periodStart);
  return interval === 'week'
    ? `w/c ${date.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', timeZone: 'UTC'})}`
    : date.toLocaleDateString('en-GB', {month: 'short', year: 'numeric', timeZone: 'UTC'});
}

/**
 Accuracy per week or month of submission, one row per vertical (or, for leads, per analyst) and an
 Overall row summed from them. Only the grouping on show is requested, so the lead-only analyst
 endpoint is never called without the permission.
 */
function AccuracyTrendTable({
  range,
  vertical,
  outcomes,
  canCompareAnalysts,
  useVerticalRows,
  useAnalystRows
}: {
  readonly range: RangeQuery;
  readonly vertical: string | null;
  readonly outcomes: readonly AccuracyOutcome[];
  readonly canCompareAnalysts: boolean;
  readonly useVerticalRows: (range: RangeQuery, enabled?: boolean) => QueryResult;
  readonly useAnalystRows: (
    query: RangeQuery & {policy_violation?: string},
    enabled: boolean
  ) => QueryResult;
}) {
  const [interval, setIntervalUnit] = useState<Interval>('week');
  const [groupBy, setGroupBy] = useState<'vertical' | 'analyst'>('vertical');
  const byAnalyst = groupBy === 'analyst' && canCompareAnalysts;

  const verticalQuery = useVerticalRows({...range, interval}, !byAnalyst);
  const analystQuery = useAnalystRows(
    {...range, interval, ...(vertical ? {policy_violation: vertical} : {})},
    byAnalyst
  );
  const query = byAnalyst ? analystQuery : verticalQuery;
  const keyField = byAnalyst ? 'analyst_email' : 'policy_violation';
  const rows = (query.data ?? []).filter(
    (row) => byAnalyst || vertical === null || row.policy_violation === vertical
  );

  const periods = [...new Set(rows.map((row) => String(row.period_start)))].sort();
  const keys = [...new Set(rows.map((row) => String(row[keyField])))];
  const cell = (key: string | null, period: string): AccuracyRow =>
    totalOf(
      rows.filter(
        (row) => String(row.period_start) === period && (key === null || row[keyField] === key)
      ),
      outcomes
    );

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm font-medium">
          {vertical ? `Accuracy over time · ${vertical}` : 'Accuracy over time'}
        </h3>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          {canCompareAnalysts ? (
            <ToggleGroup
              variant="outline"
              spacing={0}
              size="sm"
              aria-label="Group by"
              value={[groupBy]}
              onValueChange={(next) => {
                if (next[0] === 'vertical' || next[0] === 'analyst') {
                  setGroupBy(next[0]);
                }
              }}>
              <ToggleGroupItem value="vertical">By vertical</ToggleGroupItem>
              <ToggleGroupItem value="analyst">By analyst</ToggleGroupItem>
            </ToggleGroup>
          ) : null}
          <ToggleGroup
            variant="outline"
            spacing={0}
            size="sm"
            aria-label="Period"
            value={[interval]}
            onValueChange={(next) => {
              if (next[0] === 'week' || next[0] === 'month') {
                setIntervalUnit(next[0]);
              }
            }}>
            <ToggleGroupItem value="week">Weekly</ToggleGroupItem>
            <ToggleGroupItem value="month">Monthly</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{byAnalyst ? 'Analyst' : 'Vertical'}</TableHead>
              {periods.map((period) => (
                <TableHead key={period} className="text-right whitespace-nowrap">
                  {periodLabel(period, interval)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={periods.length + 1}>
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                    <Spinner />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
            {!query.isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={periods.length + 1} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <BarChart3 />
                      </EmptyMedia>
                      <EmptyTitle>No reviews yet</EmptyTitle>
                      <EmptyDescription>No reviewed items in this range.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : null}
            {rows.length > 0
              ? [null, ...keys].map((key) => (
                  <TableRow key={key ?? 'overall'}>
                    <TableCell className="text-xs font-medium">{key ?? 'Overall'}</TableCell>
                    {periods.map((period) => {
                      const total = cell(key, period);
                      return (
                        <TableCell key={period} className="text-right tabular-nums">
                          {total.total_reviewed > 0 ? (
                            <>
                              <span className="font-medium">
                                {formatPercent(total.accuracy_rate)}
                              </span>{' '}
                              <span className="text-muted-foreground text-xs">
                                ({total.total_reviewed})
                              </span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              : null}
          </TableBody>
        </Table>
      </TablePanel>
    </section>
  );
}

function Stat({label, children}: {readonly label: string; readonly children: ReactNode}) {
  return (
    <div className="bg-card border-border flex flex-col gap-1 border p-3">
      <span className="text-muted-foreground truncate text-xs">{label}</span>
      <span className="font-heading text-lg font-medium tabular-nums">{children}</span>
    </div>
  );
}

function AccuracyTable({
  title,
  nameHeader,
  isLoading,
  outcomes,
  rows,
  selectedKey,
  onSelect
}: {
  readonly title: string;
  readonly nameHeader: string;
  readonly isLoading: boolean;
  readonly outcomes: readonly AccuracyOutcome[];
  readonly rows: ReadonlyArray<AccuracyRow & {key: string}>;
  readonly selectedKey?: string | null;
  // Set for the vertical table: a row's name filters the page to that vertical.
  readonly onSelect?: (key: string) => void;
}) {
  const columnCount = outcomes.length + 3;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="font-heading text-sm font-medium">{title}</h3>
      <TablePanel>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{nameHeader}</TableHead>
              <TableHead className="text-right">Total reviewed</TableHead>
              {outcomes.map((o) => (
                <TableHead key={o.field} className="text-right">
                  {o.label}
                </TableHead>
              ))}
              <TableHead className="text-right">Accuracy</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-8">
                    <Spinner />
                    Loading…
                  </div>
                </TableCell>
              </TableRow>
            ) : null}
            {!isLoading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="p-0">
                  <Empty className="py-10">
                    <EmptyHeader>
                      <EmptyMedia variant="icon">
                        <BarChart3 />
                      </EmptyMedia>
                      <EmptyTitle>No reviews yet</EmptyTitle>
                      <EmptyDescription>No reviewed items in this range.</EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : null}
            {rows.map((row) => (
              <TableRow key={row.key} data-state={selectedKey === row.key ? 'selected' : undefined}>
                <TableCell className="text-xs font-medium">
                  {onSelect ? (
                    <button
                      type="button"
                      aria-pressed={selectedKey === row.key}
                      className="hover:text-primary-ink text-left transition-colors"
                      onClick={() => {
                        onSelect(row.key);
                      }}>
                      {row.key}
                    </button>
                  ) : (
                    row.key
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.total_reviewed.toLocaleString()}
                </TableCell>
                {outcomes.map((o) => (
                  <TableCell
                    key={o.field}
                    className={cn(
                      'text-right tabular-nums',
                      o.error &&
                        row.total_reviewed > 0 &&
                        count(row, o.field) / row.total_reviewed > ERROR_WARN_RATE &&
                        'text-destructive-ink font-medium'
                    )}>
                    {count(row, o.field).toLocaleString()}
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Progress
                      value={Math.round(row.accuracy_rate * 100)}
                      aria-label={`${row.key} accuracy`}
                      className="w-24"
                    />
                    <span className="w-12 text-right font-medium tabular-nums">
                      {formatPercent(row.accuracy_rate)}
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TablePanel>
    </section>
  );
}
